import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@shared/lib/utils';

const headingVariants = cva('font-sans tracking-normal text-base-foreground', {
  variants: {
    level: {
      h1: 'text-4xl font-extrabold leading-10 lg:text-5xl lg:leading-[48px]',
      h2: 'border-b border-base-border pb-2 text-3xl font-semibold leading-9',
      h3: 'text-2xl font-semibold leading-8',
      h4: 'text-xl font-semibold leading-7',
      pageTitle: 'text-3xl font-semibold leading-9',
    },
    spacing: {
      default: '',
      section: 'scroll-m-20',
      rich: '',
    },
  },
  defaultVariants: {
    level: 'h3',
    spacing: 'default',
  },
});

const textVariants = cva('font-sans', {
  variants: {
    variant: {
      p: 'text-base font-normal leading-6 text-base-foreground',
      lead: 'text-xl font-normal leading-7 text-base-muted-foreground',
      large: 'text-lg font-semibold leading-7 text-base-foreground',
      small: 'text-sm font-medium leading-5 text-base-foreground',
      muted: 'text-sm font-medium leading-5 text-base-muted-foreground',
    },
  },
  defaultVariants: {
    variant: 'p',
  },
});

const blockquoteVariants = cva(
  'mt-6 border-l-2 border-base-border pl-6 font-sans text-base font-normal italic leading-6 text-base-foreground'
);

const inlineCodeVariants = cva(
  'relative rounded-md bg-base-accent px-[0.3rem] py-[0.2rem] font-mono text-sm font-normal leading-5 text-base-foreground'
);

const codeBlockVariants = cva(
  'overflow-x-auto rounded-[10px] border border-base-border bg-[#18181B] p-4 font-mono text-base font-normal leading-6 text-[rgba(255,255,255,0.47)] dark:border-base-border'
);

type HeadingLevel = NonNullable<VariantProps<typeof headingVariants>['level']>;
type TextVariant = NonNullable<VariantProps<typeof textVariants>['variant']>;

type TypographyHeadingProps = React.HTMLAttributes<HTMLHeadingElement> &
  VariantProps<typeof headingVariants>;

export function TypographyH1({ className, spacing, ...props }: Omit<TypographyHeadingProps, 'level'>) {
  return (
    <h1 className={cn(headingVariants({ level: 'h1', spacing }), className)} {...props} />
  );
}

export function TypographyH2({ className, spacing, ...props }: Omit<TypographyHeadingProps, 'level'>) {
  return (
    <h2 className={cn(headingVariants({ level: 'h2', spacing }), className)} {...props} />
  );
}

export function TypographyH3({ className, spacing, ...props }: Omit<TypographyHeadingProps, 'level'>) {
  return (
    <h3 className={cn(headingVariants({ level: 'h3', spacing }), className)} {...props} />
  );
}

export function TypographyH4({ className, spacing, ...props }: Omit<TypographyHeadingProps, 'level'>) {
  return (
    <h4 className={cn(headingVariants({ level: 'h4', spacing }), className)} {...props} />
  );
}

/** Cabinet page header — Figma text-3xl/semibold without section border */
export function TypographyPageTitle({
  className,
  ...props
}: Omit<TypographyHeadingProps, 'level' | 'spacing'>) {
  return (
    <h1 className={cn(headingVariants({ level: 'pageTitle', spacing: 'default' }), className)} {...props} />
  );
}

export interface TypographyTextProps
  extends React.HTMLAttributes<HTMLParagraphElement>,
    VariantProps<typeof textVariants> {}

export function TypographyP({ className, variant = 'p', ...props }: TypographyTextProps) {
  return <p className={cn(textVariants({ variant: variant ?? 'p' }), className)} {...props} />;
}

export function TypographyLead(props: Omit<TypographyTextProps, 'variant'>) {
  return <TypographyP variant="lead" {...props} />;
}

export function TypographyLarge(props: Omit<TypographyTextProps, 'variant'>) {
  return <TypographyP variant="large" {...props} />;
}

export function TypographySmall(props: Omit<TypographyTextProps, 'variant'>) {
  return <TypographyP variant="small" {...props} />;
}

export function TypographyMuted(props: Omit<TypographyTextProps, 'variant'>) {
  return <TypographyP variant="muted" {...props} />;
}

export function TypographyBlockquote({
  className,
  ...props
}: React.BlockquoteHTMLAttributes<HTMLQuoteElement>) {
  return <blockquote className={cn(blockquoteVariants(), className)} {...props} />;
}

export function TypographyInlineCode({
  className,
  ...props
}: React.HTMLAttributes<HTMLElement>) {
  return <code className={cn(inlineCodeVariants(), className)} {...props} />;
}

export function TypographyCodeBlock({
  className,
  ...props
}: React.HTMLAttributes<HTMLPreElement>) {
  return <pre className={cn(codeBlockVariants(), className)} {...props} />;
}

/**
 * Rich text container — shadcn Typography / Rich Text spacing from Figma.
 */
export function TypographyProse({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'cabinet-prose font-sans text-base-foreground',
        '[&_h1]:text-4xl [&_h1]:font-extrabold [&_h1]:leading-10 lg:[&_h1]:text-5xl lg:[&_h1]:leading-[48px]',
        '[&_h2]:mt-10 [&_h2]:border-b [&_h2]:border-base-border [&_h2]:pb-2 [&_h2]:text-3xl [&_h2]:font-semibold [&_h2]:leading-9',
        '[&_h3]:mt-8 [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:leading-8',
        '[&_h4]:text-xl [&_h4]:font-semibold [&_h4]:leading-7',
        '[&_p]:text-base [&_p]:font-normal [&_p]:leading-6',
        '[&_p+p]:mt-6',
        '[&_blockquote]:mt-6 [&_blockquote]:border-l-2 [&_blockquote]:border-base-border [&_blockquote]:pl-6 [&_blockquote]:italic',
        '[&_ul]:my-6 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:leading-6',
        '[&_ol]:my-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:leading-6',
        '[&_li+li]:mt-2',
        '[&_table]:my-6 [&_table]:w-full [&_table]:border [&_table]:border-base-border',
        '[&_th]:border-t [&_th]:border-base-border [&_th]:px-4 [&_th]:py-2 [&_th]:text-left [&_th]:text-base [&_th]:font-bold [&_th]:leading-6',
        '[&_td]:border-t [&_td]:border-base-border [&_td]:px-4 [&_td]:py-2 [&_td]:text-base [&_td]:font-normal [&_td]:leading-6',
        '[&_tr:nth-child(even)_td]:bg-base-accent',
        className
      )}
      {...props}
    />
  );
}

export { headingVariants, textVariants, blockquoteVariants, inlineCodeVariants, codeBlockVariants };
export type { HeadingLevel, TextVariant };
